import { CircleHelp, Lock, RefreshCw, TriangleAlert, X, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Tone = "danger" | "info" | "unknown";

const toneClass: Record<Tone, { box: string; icon: string }> = {
  danger: { box: "border-danger/35 bg-danger/6 text-danger", icon: "text-danger" },
  info: { box: "border-info/35 bg-info/6 text-info", icon: "text-info" },
  unknown: {
    box: "border-dashed border-unknown/60 bg-unknown/6 text-unknown",
    icon: "text-unknown",
  },
};

/**
 * The notice the States board uses: an icon, a title, and one sentence about what happens next.
 * Colour is never the only signal. The title carries the meaning.
 */
function Notice({
  tone,
  icon: Icon,
  title,
  children,
}: {
  tone: Tone;
  icon: LucideIcon;
  title: string;
  children: ReactNode;
}) {
  const style = toneClass[tone];
  return (
    <div role="alert" className={cn("flex gap-3 rounded-lg border p-3", style.box)}>
      <Icon aria-hidden className={cn("mt-0.5 size-4 shrink-0", style.icon)} />
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <div className="text-[0.8125rem] text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

/** Skeletons keep the layout still while a project is loading. */
export function LoadingState({ label, rows = 3 }: { label: string; rows?: number }) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className="flex flex-col gap-3">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} role="presentation" className="flex items-center gap-3">
          <span className="h-4 w-22 animate-pulse rounded-md bg-muted" />
          <span className="h-4 flex-1 animate-pulse rounded-md bg-muted" />
          <span className="h-4 w-15 animate-pulse rounded-md bg-muted" />
        </div>
      ))}
    </div>
  );
}

export { EmptyState, type EmptyStateProps } from "@/components/bespoke/empty-state";

/**
 * Not connected yet state for surfaces and features that require external adapters
 * or backend capabilities that are not yet wired.
 */
export function NotConnectedState({
  title,
  description,
  badge = "Not connected yet",
  action,
}: {
  title: string;
  description: string;
  badge?: string;
  action?: { label: string; onClick?: () => void };
}) {
  return (
    <div className="flex flex-col items-center gap-2.5 rounded-lg border border-dashed p-6 text-center">
      <Badge variant="secondary" className="font-mono text-xs">
        {badge}
      </Badge>
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      <p className="max-w-md text-[0.8125rem] leading-relaxed text-muted-foreground">
        {description}
      </p>
      {action ? (
        <Button variant="outline" size="sm" onClick={action.onClick} className="mt-2">
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * A failure the person can act on. The request id is the reference support gets; the page never
 * shows a stack trace or a provider error.
 */
export function ErrorState({
  title,
  description,
  requestId,
  onRetry,
}: {
  title: string;
  description: string;
  requestId: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="danger" icon={X} title={title}>
        <p>{description}</p>
        <p>
          Reference <span className="font-mono text-foreground">{requestId}</span>
        </p>
      </Notice>
      <div>
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw aria-hidden />
          Try again
        </Button>
      </div>
    </div>
  );
}

/**
 * Accepted but not finished. A 202 never reads as success: the badge stays pending and the action
 * stays disabled until the owning service confirms it.
 */
export function PendingState({
  title,
  description,
  badge,
  action,
}: {
  title: string;
  description: string;
  badge: string;
  action: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button disabled>{action}</Button>
        <Badge variant="info">{badge}</Badge>
      </div>
      <Notice tone="info" icon={CircleHelp} title={title}>
        {description}
      </Notice>
    </div>
  );
}

/**
 * Trusted state moved while the person was reading it. Reload comes first; deciding again waits.
 */
export function ConflictState({
  title,
  description,
  onReload,
}: {
  title: string;
  description: string;
  onReload: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="danger" icon={X} title={title}>
        {description}
      </Notice>
      <div className="flex gap-2">
        <Button onClick={onReload}>
          <RefreshCw aria-hidden />
          Reload and review
        </Button>
        <Button variant="outline" disabled>
          Approve
        </Button>
      </div>
    </div>
  );
}

/**
 * A 403 clears the content first and explains second. Children are accepted and deliberately not
 * rendered, so stale rows cannot survive the response.
 */
export function ForbiddenState({
  title,
  description,
  action,
  children: _children,
}: {
  title: string;
  description: string;
  action?: { label: string; onClick?: () => void };
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-2 py-6 text-center">
      <Lock aria-hidden className="size-5 text-muted-foreground" />
      <h2 className="text-sm font-medium text-foreground">{title}</h2>
      <p className="max-w-sm text-[0.8125rem] text-muted-foreground">{description}</p>
      {action ? (
        <Button variant="outline" onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * An unknown effect or spend stays on screen until it is reconciled. It is never hidden, never
 * edited, and never shown as zero, and it offers no retry unless a safe replay is proven.
 */
export function UnknownOutcome({
  title,
  description,
  amount,
}: {
  title: string;
  description: string;
  amount: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="unknown" icon={TriangleAlert} title={title}>
        {description}
      </Notice>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="unknown">Unknown</Badge>
        <Badge variant="unknown">{amount}</Badge>
      </div>
    </div>
  );
}
